import { AnalyticsConfig } from '../mm/types/analytics.types';
import { fetchUtils } from 'react-admin';
import { getConfig } from '../../../framework/config/app.config';

/**
 * Utility function to normalize string objects back to primitive strings
 * This fixes the issue where JSON imports convert strings to String objects
 * which appear as character arrays {0: 'c', 1: 'a', 2: 's', ...}
 */
function normalizeStringObjects(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  // Handle String objects - convert to primitive string
  if (typeof obj === 'object' && obj.constructor === String) {
    return obj.valueOf();
  }
  
  // Handle objects that look like String objects (character arrays)
  if (typeof obj === 'object' && !Array.isArray(obj)) {
    const keys = Object.keys(obj);
    const isStringObject = keys.length > 0 && keys.every((key, index) => {
      return key === index.toString() && typeof obj[key] === 'string' && obj[key].length === 1;
    });
    
    if (isStringObject) {
      // Convert character array back to string
      return keys.map(key => obj[key]).join('');
    }
  }
  
  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => normalizeStringObjects(item));
  }
  
  // Handle regular objects
  if (typeof obj === 'object') {
    const normalized: any = {};
    for (const [key, value] of Object.entries(obj)) {
      normalized[key] = normalizeStringObjects(value);
    }
    return normalized;
  }
  
  // Return primitive values as-is
  return obj;
}

// 🔧 NEW: Server-side template loading - no more client-side JSON imports!

// Type for template metadata
export interface TemplateMetadata {
  id: string;
  name: string;
  description: string;
  category: string;
  fileName: string;
  tags: string[];
}

export interface TemplateIndex {
  metadata: {
    title: string;
    description: string;
    exportDate: string;
    version: string;
  };
  examples: TemplateMetadata[];
}

/**
 * Analytics Template Service
 * 
 * 🔧 NEW: Server-side template loading using React Admin data provider pattern
 * Uses React Admin's fetchUtils and config system for consistent behavior
 */
export class AnalyticsTemplateService {
  // Cache for full template configurations to avoid redundant API calls
  private templateCache: Map<string, AnalyticsConfig> = new Map();
  private templateMetadataCache: TemplateMetadata[] | null = null;
  
  /**
   * Get the base URL for API calls
   */
  private getBaseUrl(): string {
    return sessionStorage.getItem('selectedEnvUrl');
    //return 'http://localhost:3000';
  }
  
  /**
   * Make an authenticated API call using React Admin's fetchUtils
   * This follows the same pattern as the existing data provider
   */
  private async makeApiCall(endpoint: string): Promise<any> {
    try {
      const baseUrl = this.getBaseUrl();
      
      // If no base URL is configured, return empty result instead of failing
      if (!baseUrl) {
        console.warn(`⚠️ [AnalyticsTemplateService] No base URL configured, skipping API call to ${endpoint}`);
        return null;
      }
      
      const apiUrl = `${baseUrl}/analytics${endpoint}`;
      
      console.log(`🔍 [AnalyticsTemplateService] API call to: ${apiUrl}`);
      
      // 🔧 Use React Admin's fetchUtils for consistent authentication and error handling
      const { json } = await fetchUtils.fetchJson(apiUrl, {
        method: 'GET',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
      });
      
      return json;
      
    } catch (error) {
      // Don't throw - return null to allow app to continue without templates
      console.warn(`⚠️ [AnalyticsTemplateService] API call failed for ${endpoint} (backend may not be running):`, error);
      return null;
    }
  }
  
  /**
   * Load a template configuration by ID from the server
   * Uses React Admin's data provider pattern for consistent API calls
   */
  async loadTemplate(templateId: string): Promise<AnalyticsConfig | null> {
    console.log(`🔍 [AnalyticsTemplateService] Loading template: ${templateId}`);
    
    // 🚀 NEW: Check cache first to avoid redundant API call
    const cachedTemplate = this.templateCache.get(templateId);
    if (cachedTemplate) {
      console.log(`💾 [AnalyticsTemplateService] Using cached template configuration: ${templateId}`);
      
      // 🧪 DEBUG: Check string conversion for sales dashboard templates (from cache)
      if (templateId.includes('sales-dashboard')) {
        const testViz = cachedTemplate?.tabs?.[0]?.visualizations?.[0];
        if ((testViz?.config as any)?.defaultValue) {
          console.log(`🧪 [CACHED - ${templateId}] DefaultValue array:`, (testViz.config as any).defaultValue);
          console.log(`🧪 [CACHED - ${templateId}] First defaultValue type:`, typeof (testViz.config as any).defaultValue[0]);
          console.log(`🧪 [CACHED - ${templateId}] First defaultValue value:`, (testViz.config as any).defaultValue[0]);
          console.log(`🧪 [CACHED - ${templateId}] Is primitive string?:`, typeof (testViz.config as any).defaultValue[0] === 'string');
        }
      }
      
      return cachedTemplate;
    }
    
    // 🔄 If not in cache, ensure templates are loaded first (this will populate the cache)
    console.log(`⚠️ [AnalyticsTemplateService] Template ${templateId} not in cache, loading all templates first...`);
    await this.getAvailableTemplates();
    
    // Try cache again after loading all templates
    const cachedAfterLoad = this.templateCache.get(templateId);
    if (cachedAfterLoad) {
      console.log(`✅ [AnalyticsTemplateService] Found template in cache after loading all templates: ${templateId}`);
      return cachedAfterLoad;
    }
    
    // 🔥 FALLBACK: If still not found, make individual API call (should rarely happen)
    console.log(`⚠️ [AnalyticsTemplateService] Template ${templateId} still not found, making individual API call...`);
    try {
      const templateConfig = await this.makeApiCall(`/templates/${templateId}`);
      
      // Check for server-side errors
      if (templateConfig?.error) {
        console.error(`Server error loading template ${templateId}:`, templateConfig.error);
        return null;
      }

      console.log(`🔍 [AnalyticsTemplateService] Raw template from server (fallback):`, templateConfig);
      
      // Apply normalization
      const normalizedConfig = normalizeStringObjects(templateConfig);
      
      // Cache for future use
      this.templateCache.set(templateId, normalizedConfig as AnalyticsConfig);

      if (!normalizedConfig || !normalizedConfig.id) {
        console.error(`Invalid configuration for template: ${templateId}`);
        return null;
      }

      console.log(`✅ [AnalyticsTemplateService] Successfully loaded template via fallback: ${templateId}`);
      return normalizedConfig as AnalyticsConfig;
      
    } catch (error) {
      console.error(`❌ [AnalyticsTemplateService] Failed to load template ${templateId} from server:`, error);
      return null;
    }
  }
  
  /**
   * Get all available template metadata from the server
   * Uses React Admin's data provider pattern for consistent API calls
   */
  async getAvailableTemplates(): Promise<TemplateMetadata[]> {
    // Return cached metadata if available
    if (this.templateMetadataCache) {
      console.log(`💾 [AnalyticsTemplateService] Using cached template metadata (${this.templateMetadataCache.length} templates)`);
      return this.templateMetadataCache;
    }

    try {
      console.log(`🔍 [AnalyticsTemplateService] Fetching available templates from server`);
      
      const data = await this.makeApiCall('/templates');
      
      // If API call failed or returned null, return empty array
      if (!data || data?.error) {
        if (data?.error) {
          console.error(`Server error fetching templates:`, data.error);
        }
        return [];
      }

      console.log(`🔍 [AnalyticsTemplateService] Server response structure:`, Object.keys(data));
      
      // Handle the server response format: { metadata: {...}, templates: { "template-id": {...}, ... } }
      if (data?.templates && typeof data.templates === 'object') {
        // Cache full template configurations and extract metadata
        const templateMetadata: TemplateMetadata[] = Object.entries(data.templates)
          .filter(([key, template]: [string, any]) => template && (template.id || key)) // Filter out invalid templates
          .map(([key, template]: [string, any]) => {
            const templateId = template.id || key;
            
            // 🚀 NEW: Cache the full template configuration to avoid second API call
            if (template && typeof template === 'object') {
              // Apply normalization to cached template
              const normalizedTemplate = normalizeStringObjects(template);
              this.templateCache.set(templateId, normalizedTemplate as AnalyticsConfig);
              console.log(`💾 [AnalyticsTemplateService] Cached full template configuration: ${templateId}`);
            }
            
            return {
              id: templateId,
              name: template.name || template.id || key,
              description: template.description || '',
              category: template.category || 'general',
              fileName: `${template.id || key}.json`,
              tags: template.tags || []
            };
          });
        
        // Cache the metadata for future calls
        this.templateMetadataCache = templateMetadata;
        
        console.log(`✅ [AnalyticsTemplateService] Found ${templateMetadata.length} templates`);
        console.log(`🔍 [AnalyticsTemplateService] Template IDs:`, templateMetadata.map(t => t.id));
        console.log(`💾 [AnalyticsTemplateService] Cached ${this.templateCache.size} full template configurations`);
        return templateMetadata;
      } 
      
      // Fallback: check if it's in the expected format with examples array
      if (data?.metadata?.examples && Array.isArray(data.metadata.examples)) {
        console.log(`✅ [AnalyticsTemplateService] Found ${data.metadata.examples.length} templates (examples format)`);
        return data.metadata.examples;
      }

      console.warn(`⚠️ [AnalyticsTemplateService] Unexpected server response format:`, data);
      return [];
      
    } catch (error) {
      console.error(`❌ [AnalyticsTemplateService] Failed to fetch templates from server:`, error);
      return [];
    }
  }
  
  /**
   * Get template metadata by ID from the server
   */
  async getTemplateMetadata(templateId: string): Promise<TemplateMetadata | null> {
    const templates = await this.getAvailableTemplates();
    return templates.find(example => example.id === templateId) || null;
  }
  
  /**
   * Get templates by category from the server
   */
  async getTemplatesByCategory(category: string): Promise<TemplateMetadata[]> {
    const templates = await this.getAvailableTemplates();
    return templates.filter(example => example.category === category);
  }
  
  /**
   * Get all available categories from the server
   */
  async getTemplateCategories(): Promise<string[]> {
    const templates = await this.getAvailableTemplates();
    const categories = new Set(templates.map(template => template.category));
    return Array.from(categories).sort();
  }
  
  /**
   * Search templates by tag from the server
   */
  async getTemplatesByTag(tag: string): Promise<TemplateMetadata[]> {
    const templates = await this.getAvailableTemplates();
    return templates.filter(example => 
      example.tags.includes(tag.toLowerCase())
    );
  }
  
  /**
   * Search templates by name or description from the server
   */
  async searchTemplates(query: string): Promise<TemplateMetadata[]> {
    const templates = await this.getAvailableTemplates();
    const searchTerm = query.toLowerCase();
    return templates.filter(example => 
      example.name.toLowerCase().includes(searchTerm) ||
      example.description.toLowerCase().includes(searchTerm) ||
      example.tags.some(tag => tag.toLowerCase().includes(searchTerm))
    );
  }
  
  /**
   * Get template options formatted for React Admin SelectInput from the server
   */
  async getTemplateOptions(): Promise<Array<{ id: string; name: string }>> {
    const templates = await this.getAvailableTemplates();
    return templates.map(template => ({
      id: template.id,
      name: `${template.name}`
    }));
  }
  
  /**
   * Clear template cache (useful for testing or forcing refresh)
   */
  clearCache(): void {
    this.templateCache.clear();
    this.templateMetadataCache = null;
    console.log(`🗑️ [AnalyticsTemplateService] Cleared template cache`);
  }
  
  /**
   * Force refresh templates (clears cache and reloads)
   */
  async forceRefresh(): Promise<void> {
    console.log(`🔄 [AnalyticsTemplateService] Force refreshing templates...`);
    this.clearCache();
    await this.getAvailableTemplates();
    console.log(`✅ [AnalyticsTemplateService] Templates refreshed`);
  }
  
  /**
   * Check if templates are loaded in cache
   */
  areTemplatesLoaded(): boolean {
    return this.templateMetadataCache !== null && this.templateCache.size > 0;
  }
}

// Create a singleton instance for use throughout the application
export const analyticsTemplateService = new AnalyticsTemplateService();

// 🔧 BACKWARD COMPATIBILITY: Export functions that delegate to the service instance
// This maintains existing function-based API while using the new service underneath

/**
 * Load a template configuration by ID from the server
 * 
 * 🔧 NEW: Server-side template loading eliminates client-side JSON import issues
 * Templates are loaded from the server which reads them directly from the file system
 * without any bundler string conversion problems.
 */
export async function loadTemplate(templateId: string): Promise<AnalyticsConfig | null> {
  return analyticsTemplateService.loadTemplate(templateId);
}

/**
 * Get all available template metadata from the server
 */
export async function getAvailableTemplates(): Promise<TemplateMetadata[]> {
  return analyticsTemplateService.getAvailableTemplates();
}

/**
 * Get template metadata by ID from the server
 */
export async function getTemplateMetadata(templateId: string): Promise<TemplateMetadata | null> {
  return analyticsTemplateService.getTemplateMetadata(templateId);
}

/**
 * Get templates by category from the server
 */
export async function getTemplatesByCategory(category: string): Promise<TemplateMetadata[]> {
  return analyticsTemplateService.getTemplatesByCategory(category);
}

/**
 * Get all available categories from the server
 */
export async function getTemplateCategories(): Promise<string[]> {
  return analyticsTemplateService.getTemplateCategories();
}

/**
 * Search templates by tag from the server
 */
export async function getTemplatesByTag(tag: string): Promise<TemplateMetadata[]> {
  return analyticsTemplateService.getTemplatesByTag(tag);
}

/**
 * Search templates by name or description from the server
 */
export async function searchTemplates(query: string): Promise<TemplateMetadata[]> {
  return analyticsTemplateService.searchTemplates(query);
}

/**
 * Get template options formatted for React Admin SelectInput from the server
 */
export async function getTemplateOptions(): Promise<Array<{ id: string; name: string }>> {
  return analyticsTemplateService.getTemplateOptions();
}

/**
 * Clear template cache and force refresh (for debugging)
 */
export async function forceRefreshTemplates(): Promise<void> {
  return analyticsTemplateService.forceRefresh();
}

/**
 * Validate a template configuration
 */
export function validateTemplate(config: AnalyticsConfig): boolean {
  try {
    // Basic validation - ensure required fields exist
    return !!(
      config.id &&
      config.name &&
      config.tabs &&
      config.tabs.length > 0
    );
  } catch {
    return false;
  }
} 